import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IMessage, useStompClient, useSubscription } from 'react-stomp-hooks';

import { useUserSessionData } from '../router/selectors';
import { useMount } from './useMount';

export interface IMessaging {
  [key: string]: string;
}

/** Safely parse a STOMP frame body. On failure, surfaces to Sentry and returns
 *  null — callers must null-check and skip the frame rather than throwing
 *  inside the subscription callback (which would silently kill the
 *  subscription the same way the queueing variant's original bug did). */
const safeParseStompBody = <T = IMessaging>(
  body: string,
  source: string,
  topic: string
): T | null => {
  try {
    return JSON.parse(body) as T;
  } catch (err) {
    Sentry.captureException(err, {
      tags: { source, topic },
      extra: {
        bodyPreview:
          typeof body === 'string' ? body.slice(0, 500) : '[non-string body]',
      },
    });
    return null;
  }
};

export const useSocketJSMessageGeneral = (
  type: string,
  isSkipCurrentUser = false
): [IMessaging | undefined, () => void] => {
  const { id = '' } = useUserSessionData() ?? {};
  const topicNameWithoutId = useMemo(() => `/topic/${type}`, [type]);
  const [messages, setMessage] = useState<IMessaging>();

  useSubscription(
    topicNameWithoutId,
    (message) => {
      const body = safeParseStompBody<IMessaging>(
        message.body,
        'useSocketJSMessageGeneral',
        type
      );
      if (!body) return;
      if (!isSkipCurrentUser || !body['userId'] || body['userId'] !== id) {
        setMessage(body);
      }
    },
    {}
  );

  const clearMessage = useCallback(() => {
    setMessage(undefined);
  }, []);

  return [messages, clearMessage];
};

/**
 * Queueing variant of `useSocketJSMessageGeneral`. Use when a single consumer
 * needs every frame in a burst (e.g. multiple employees clocking in within
 * one tick at shift start). The single-message variant coalesces bursts to
 * the latest frame; this one accumulates into an array and returns a
 * `flush()` that returns and clears the buffered frames.
 */
export const useSocketJSMessageQueue = (
  type: string,
  isSkipCurrentUser = false
): [IMessaging[], () => IMessaging[]] => {
  const { id = '' } = useUserSessionData() ?? {};
  const topicName = useMemo(() => `/topic/${type}`, [type]);
  const queueRef = useRef<IMessaging[]>([]);
  const [, forceRender] = useState(0);

  useSubscription(
    topicName,
    (message) => {
      const body = safeParseStompBody<IMessaging>(
        message.body,
        'useSocketJSMessageQueue',
        type
      );
      if (!body) return;
      if (isSkipCurrentUser && body['userId'] && body['userId'] === id) return;
      queueRef.current.push(body);
      // Bump a counter to wake the consumer's effect for draining.
      forceRender((c) => c + 1);
    },
    {}
  );

  const flush = useCallback((): IMessaging[] => {
    const drained = queueRef.current;
    queueRef.current = [];
    return drained;
  }, []);

  return [queueRef.current, flush];
};

export const useSockJSMessage = (
  type: string,
  isSkipCurrentUser = false
): [IMessaging | undefined, () => void, () => void] => {
  const client = useStompClient();
  const { id = '' } = useUserSessionData() ?? {};
  const [messages, setMessage] = useState<IMessaging>();

  const topicName = useMemo(() => `/topic/${type}/${id}`, [id, type]);

  const handleDisconnect = useCallback(() => {
    if (client && client.active) {
      client.deactivate();
    }
  }, [client]);

  useSubscription(
    topicName,
    (message) => {
      const body = safeParseStompBody<IMessaging>(
        message.body,
        'useSockJSMessage',
        type
      );
      if (!body) return;
      if (!isSkipCurrentUser || !body['userId'] || body['userId'] !== id) {
        setMessage(body);
      }
    },
    {}
  );

  const clearMessage = useCallback(() => {
    setMessage(undefined);
  }, []);

  return [messages, clearMessage, handleDisconnect];
};

export const useSockJSMessageRecordRef = (
  type: string,
  key: string
): [Record<string, any> | undefined] => {
  const { id = '' } = useUserSessionData() ?? {};
  const topicName = useMemo(() => `/topic/${type}/${id}`, [id, type]);

  const [updated, setUpdated] = useState<number>(0);
  const messagesOriginalRef = useRef<string[]>([]);
  const messagesRecordRef = useRef<Record<string, any>>({});

  useSubscription(topicName, (message) => {
    messagesOriginalRef.current.push(message.body);
    setUpdated(Math.random());
  });

  useEffect(() => {
    if (updated) {
      messagesOriginalRef.current.map((message) => {
        const item = safeParseStompBody<Record<string, any>>(
          message,
          'useSockJSMessageRecordRef',
          type
        );
        if (item && item[key]) {
          messagesRecordRef.current[item[key]] = item;
        }
      });
      setUpdated(0);
    }
  }, [key, type, updated]);

  useMount(() => {
    return () => {
      messagesOriginalRef.current = [];
      messagesRecordRef.current = {};
    };
  });

  return [messagesRecordRef.current];
};

/**
 * A reusable hook for subscribing to a WebSocket topic using react-stomp-hooks.
 *
 * @param topic The WebSocket topic to subscribe to.
 * @returns An array containing the current messages and a reset function.
 */

export const useSocketSubscription = <T>(
  type: string
): [T | undefined, () => void, () => void] => {
  const client = useStompClient();
  const { id = '' } = useUserSessionData() ?? {};
  const topicName = useMemo(() => `/topic/${type}/${id}`, [id, type]);

  const [messages, setMessages] = useState<T>();

  const handleDisconnect = useCallback(() => {
    if (client && client.active) {
      client.deactivate();
    }
  }, [client]);

  const handleMessage = useCallback(
    (message: IMessage) => {
      const parsed = safeParseStompBody<T>(
        message.body,
        'useSocketSubscription',
        type
      );
      if (parsed === null) {
        // Parse failed — clear any stale value so consumers don't operate on a
        // raw string cast as their expected type. Next valid frame recovers.
        setMessages(undefined);
        return;
      }
      setMessages(parsed);
    },
    [type]
  );

  useSubscription(topicName, handleMessage);

  const resetMessages = useCallback(() => {
    setMessages(undefined);
  }, []);

  return [messages, resetMessages, handleDisconnect];
};
